/**
 * 気象サービス：6時間ごとにWorkerから取得（§2）、失敗時は縮退（§6.4）。
 * 縮退時は晴天モデル（clearSkyUviApprox）＝被曝を多めに見積もる側＝安全側。
 */
import {
  clearSkyUviApprox,
  interpolateWx,
  solarPosition,
  wxCellId,
} from "@uvk/shared";
import type { LatLon, Ms, WxMinute, WxResponse } from "@uvk/shared";
import { db } from "./db.js";
import { fetchWx } from "./api.js";

const REFRESH_MS = 6 * 3600 * 1000;
const STALE_LIMIT_MS = 12 * 3600 * 1000; // 最終良値の延命上限（§6.4）

let cache: WxResponse | null = null;
let cacheCell = "";

export async function ensureWx(pos: LatLon, now: Ms): Promise<void> {
  const cell = wxCellId(pos);
  if (cache && cacheCell === cell && now - cache.fetchedAt < REFRESH_MS && !cache.degraded) return;

  const fresh = await fetchWx(cell);
  if (fresh && !("error" in fresh)) {
    cache = fresh;
    cacheCell = cell;
    await db.put("kv", fresh, `wx:${cell}`);
    return;
  }
  if (!cache || cacheCell !== cell) {
    const stored = await db.get<WxResponse>("kv", `wx:${cell}`);
    if (stored) {
      cache = stored;
      cacheCell = cell;
    }
  }
}

/** 現在tickの気象。縮退の有無を含めて返す */
export function wxAt(pos: LatLon, now: Ms): WxMinute {
  if (cache && cacheCell === wxCellId(pos) && now - cache.fetchedAt < STALE_LIMIT_MS) {
    return interpolateWx(cache.hourly, now, cache.degraded);
  }
  // 晴天モデルへ縮退（§6.4）：UVIc相当を晴天式で近似。fd_bbは快晴相当0.2
  const sun = solarPosition(now, pos);
  const uvi = clearSkyUviApprox(sun.elevDeg);
  // 縮退時は大気汚染も分からない。0を入れるが、これは「汚染なし」ではなく「不明」なので
  // 肌年齢の推定では上乗せしない側に倒れる（数字を作らない）
  return { uvi, uviClearSky: uvi, fdBb: 0.2, cmf: uvi > 0 ? 1 : 0, tempC: 28, rh: 60, pm25: 0, degraded: true };
}

export function isDegraded(pos: LatLon, now: Ms): boolean {
  return wxAt(pos, now).degraded;
}
